import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, Filter, RefreshCcw, Plane, Tag as TagIcon } from 'lucide-react';
import LegalLayout from '../components/LegalLayout';
import SpecialDealCard from '../components/home/SpecialDealCard';
import PaxQuickPicker from '../components/home/PaxQuickPicker';
import { dealsCacheGet, dealsCacheSet } from '../utils/dealsCache';

const API = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: 'all', label: 'All', params: {} },
  { key: 'oneway', label: 'One-way', params: { ticket_type: 'oneway' } },
  { key: 'roundtrip', label: 'Round-trip', params: { ticket_type: 'roundtrip' } },
  { key: 'domestic', label: 'Domestic', params: { journey_scope: 'domestic' } },
  { key: 'international', label: 'International', params: { journey_scope: 'international' } },
];

/**
 * /deals — public listing page for all upcoming offline-block deals.
 * Shares <SpecialDealCard /> with the homepage so visuals stay identical.
 * Reuses <LegalLayout /> for the unified hero band + breadcrumb + footer.
 */
const DealsPage = ({ onBookDeal }) => {
  const [deals, setDeals] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');
  const [occasion, setOccasion] = useState('');
  const [labels, setLabels] = useState([]);
  const [colorHex, setColorHex] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pickerState, setPickerState] = useState({ open: false, deal: null, preselectedDate: null });

  // Load active occasion labels once (chip row populator)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/occasion-labels`, { timeout: 8000 });
        if (cancelled) return;
        setLabels(r.data?.labels || []);
        setColorHex(r.data?.color_hex || {});
      } catch (e) {
        // Non-fatal — chips just won't render.
        console.warn('Could not load occasion labels:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchDeals = useCallback(async (resetPage = false) => {
    setLoading(true);
    setError(null);
    const targetPage = resetPage ? 1 : page;
    const activeFilter = FILTERS.find((f) => f.key === filter) || FILTERS[0];
    const cacheKey = `all_${filter}_occ-${occasion || 'none'}_p${targetPage}`;
    const cached = reloadKey === 0 ? dealsCacheGet(cacheKey) : null;
    if (cached) {
      setDeals(resetPage ? cached.deals : [...(deals || []), ...cached.deals]);
      setHasMore(cached.has_more);
      setTotal(cached.total);
      setLoading(false);
      if (resetPage) setPage(1);
      return;
    }
    try {
      const params = { page: targetPage, limit: 24, ...activeFilter.params };
      if (occasion) params.occasion_label = occasion;
      const resp = await axios.get(`${API}/api/public/offline-deals/all`, {
        params,
        timeout: 10000,
      });
      const fresh = resp.data || { deals: [], total: 0, has_more: false };
      const next = resetPage ? fresh.deals : [...(deals || []), ...fresh.deals];
      setDeals(next);
      setHasMore(!!fresh.has_more);
      setTotal(fresh.total || 0);
      dealsCacheSet(cacheKey, fresh);
      if (resetPage) setPage(1);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load deals');
      if (resetPage) setDeals([]);
    } finally {
      setLoading(false);
    }
    // deps deliberately exclude `deals` to avoid loop; we use the prior closure
    // value only inside the resetPage=false branch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, occasion, page, reloadKey]);

  // Re-fetch on filter / occasion change (resets pagination)
  useEffect(() => {
    setPage(1);
    fetchDeals(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, occasion, reloadKey]);

  const handleBook = (deal, preselectedDate) => {
    if (onBookDeal) {
      onBookDeal(deal, preselectedDate);
      return;
    }
    setPickerState({ open: true, deal, preselectedDate: preselectedDate || null });
  };

  const loadMore = () => {
    setPage((p) => p + 1);
    fetchDeals(false);
  };

  return (
    <LegalLayout
      title="Special Deals"
      subtitle="Curated block-ticket fares with guaranteed seats — transparent pricing, instant booking. Limited inventory across our airline partner network."
      breadcrumbLabel="Travel"
      canonicalPath="/deals"
      description="ACF Tourism Special Deals — guaranteed-seat offline block fares with transparent pricing across Iran Air, Mahan Air, flydubai, and more."
      testId="deals-page"
    >
      {/* Filter chip row */}
      <div className="not-prose flex flex-wrap items-center gap-2 mb-6">
        <Filter className="w-4 h-4 text-[#659A9B]" />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filter === f.key
                ? 'bg-[#1B375B] text-white border-[#1B375B]'
                : 'bg-white text-[#335E77] border-[#659A9B]/30 hover:bg-[#F5FBFB]'
            }`}
            data-testid={`deals-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        {!loading && deals !== null && (
          <span className="ml-auto text-xs text-gray-500" data-testid="deals-total">
            {total} {total === 1 ? 'deal' : 'deals'}
          </span>
        )}
      </div>

      {/* Occasion chip row — only rendered when at least one active label exists */}
      {labels.length > 0 && (
        <div
          className="not-prose flex flex-wrap items-center gap-2 mb-6"
          data-testid="deals-occasion-row"
        >
          <TagIcon className="w-4 h-4 text-[#659A9B]" />
          <button
            key="occasion-all"
            type="button"
            onClick={() => setOccasion('')}
            data-testid="deals-occasion-all"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              occasion === ''
                ? 'bg-[#1B375B] text-white border-[#1B375B]'
                : 'bg-white text-[#335E77] border-[#659A9B]/30 hover:bg-[#F5FBFB]'
            }`}
          >
            All occasions
          </button>
          {labels.map((lab) => {
            const active = occasion === lab.slug;
            const hex = colorHex[lab.color] || '#1B375B';
            return (
              <button
                key={lab.slug}
                type="button"
                onClick={() => setOccasion(active ? '' : lab.slug)}
                data-testid={`deals-occasion-${lab.slug}`}
                title={lab.description || lab.name}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  active ? 'text-white' : 'bg-white text-[#335E77] hover:bg-[#F5FBFB]'
                }`}
                style={active
                  ? { backgroundColor: hex, borderColor: hex }
                  : { borderColor: `${hex}55` }}
              >
                {lab.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Loading state */}
      {loading && (deals === null || deals.length === 0) && (
        <div className="not-prose grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#659A9B]/15 h-72 animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="not-prose text-center py-12">
          <Sparkles className="w-10 h-10 mx-auto text-[#659A9B]/40 mb-3" />
          <p className="text-sm text-gray-600 mb-3">{error}</p>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#335E77] hover:text-[#1B375B]"
            data-testid="deals-retry"
          >
            <RefreshCcw className="w-4 h-4" />
            Try again
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && deals && deals.length === 0 && (
        <div className="not-prose text-center py-16">
          <Plane className="w-12 h-12 mx-auto text-[#659A9B]/40 mb-4" />
          <h3 className="text-lg font-semibold text-[#1B375B] mb-2">No special deals available right now</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Our team curates these fares from authorized airline partners.
            Check back soon or search for live fares from the homepage.
          </p>
        </div>
      )}

      {/* Grid */}
      {deals && deals.length > 0 && (
        <div className="not-prose grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="deals-grid">
          {deals.map((deal) => (
            <SpecialDealCard key={deal.id} deal={deal} onBook={handleBook} />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasMore && !loading && (
        <div className="not-prose mt-8 text-center">
          <button
            type="button"
            onClick={loadMore}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-[#659A9B]/40 text-[#1B375B] font-semibold rounded-lg hover:bg-[#F5FBFB] transition-colors"
            data-testid="deals-load-more"
          >
            Load more deals
          </button>
        </div>
      )}

      <PaxQuickPicker
        deal={pickerState.deal}
        preselectedDate={pickerState.preselectedDate}
        open={pickerState.open}
        onOpenChange={(o) => setPickerState((s) => ({ ...s, open: o }))}
      />
    </LegalLayout>
  );
};

export default DealsPage;
